import { useEffect, useRef, useState } from 'react';
import { Animated, Easing } from 'react-native';
import { useSelector } from 'react-redux';
import type { AccountInfo } from '@azure/msal-browser';

import { navigateTo, Popup, getSecureValue } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import type { RootState } from '@store/types';
import { msalInstance } from '@config/msalConfig';
import { switchWebAccount, addWebAccount } from '@reducers/user/webMultiAccount';
import { unlogin } from '@reducers/utils';
import InviteUserModal from '@components/settings/v2/structure/users/users/inviteUser';
import AddMachineModal from '@components/settings/v2/structure/machines/machines/addMachine/index';

import type { ProfileDropdownProps } from '@components/webHeader/profile/ProfileDropdown';

export interface UseManageProfileDropdownParams {
  triggerOverlay: ProfileDropdownProps['triggerOverlay'];
}

export const getAccountInitial = (account: AccountInfo): string =>
  (account.name || account.username || '?')[0].toUpperCase();

export const useManageProfileDropdown = ({ triggerOverlay }: UseManageProfileDropdownParams) => {
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const [accountsExpanded, setAccountsExpanded] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState<string | false>(false);
  const expandAnim = useRef(new Animated.Value(0)).current;
  // Synchronous guard — a useState flag would race on double-click because setState is
  // async. Without this, MSAL throws interaction_in_progress on the second loginPopup.
  const addingAccountRef = useRef(false);

  useEffect(() => {
    Animated.timing(expandAnim, {
      toValue: accountsExpanded ? 1 : 0,
      duration: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [accountsExpanded]);

  const { userProfile, emailAddress = '' } = userInfo || {};
  const { firstName = '', lastName = '' } = (userProfile as Record<string, string>) || {};

  const allAccounts = msalInstance.getAllAccounts();
  const activeAccountId = getSecureValue('_sf_active_account_id');
  const otherAccounts = allAccounts.filter((a) => a.homeAccountId !== activeAccountId);
  const hasMultipleAccounts = otherAccounts.length > 0;
  const toggleAccounts = () => setAccountsExpanded((prev) => !prev);

  const handleSwitchAccount = async (homeAccountId: string) => {
    setSwitchingAccount(homeAccountId);
    try {
      await dispatch(switchWebAccount(homeAccountId));
      triggerOverlay();
    } catch (e) {
      // reinitializeAppState already cleared Redux; Authorized would render stuck.
      // Drop back to a clean login screen so the user can retry.
      triggerOverlay();
      dispatch(unlogin());
    } finally {
      setSwitchingAccount(false);
    }
  };

  const handleAddAccount = async () => {
    if (addingAccountRef.current) return;
    addingAccountRef.current = true;
    triggerOverlay();
    try {
      await dispatch(addWebAccount());
    } catch (e) {
      dispatch(unlogin());
    } finally {
      addingAccountRef.current = false;
    }
  };

  const handleLogOff = () => {
    triggerOverlay();
    dispatch(unlogin());
  };

  const handleLogOffAll = () => {
    triggerOverlay();
    dispatch(unlogin({ clearAll: true }));
  };

  const handleAddMachine = () => {
    Popup({
      Component: AddMachineModal,
      width: 580,
    });
    triggerOverlay();
  };

  const handleAddDevice = () => {
    triggerOverlay();
    navigateTo('AddDevice');
  };

  const handleInviteColleague = () => {
    Popup({
      Component: InviteUserModal,
    });
    triggerOverlay();
  };

  return {
    firstName,
    lastName,
    emailAddress,
    allAccounts,
    activeAccountId,
    otherAccounts,
    hasMultipleAccounts,
    accountsExpanded,
    expandAnim,
    switchingAccount,
    toggleAccounts,
    handleSwitchAccount,
    handleAddAccount,
    handleLogOff,
    handleLogOffAll,
    handleAddMachine,
    handleAddDevice,
    handleInviteColleague,
  };
};
